// Parser for the BLE Heart Rate Measurement characteristic (0x2A37).
// Spec: Bluetooth SIG Heart Rate Service v1.0, Heart Rate Measurement.
//
// Packet layout:
//   byte 0 = flags
//     bit 0 = Heart Rate Value format (0 → uint8, 1 → uint16 LE)
//     bits 1-2 = Sensor Contact status
//                00 = feature not supported
//                01 = feature supported, no contact detected
//                10 = feature supported, contact detected (= sensor on skin)
//                11 = feature supported, contact detected — also valid
//     bit 3 = Energy Expended field present (uint16 LE, kJ)
//     bit 4 = RR-Interval field present (uint16 LE × 1/1024 s, repeated)
//   then: HR (1 or 2 bytes), optional energy (2 bytes), optional RR list
//
// DEFENSIVE: Length-guarded. Truncated payloads return null for fields that
// don't fit, rather than throwing — Garmin straps in particular have been
// observed shipping partial packets during low-battery states.

export interface HeartRateMeasurement {
  bpm: number | null;
  /**
   * Sensor contact:
   *   "not_supported" — sensor doesn't report contact
   *   "no_contact"    — sensor supports it but isn't touching skin
   *   "contact"       — sensor reports contact (HR likely valid)
   */
  sensor_contact: "not_supported" | "no_contact" | "contact";
  energy_kj: number | null;
  /** RR intervals in seconds (converted from spec's 1/1024 s units). */
  rr_intervals_s: number[];
}

const fits = (view: DataView, offset: number, size: number): boolean =>
  offset + size <= view.byteLength;

const decodeSensorContact = (
  flags: number,
): HeartRateMeasurement["sensor_contact"] => {
  // Spec mapping for bits 1-2:
  //   00, 01 → feature not supported by this sensor
  //   10     → supported, contact NOT detected (strap off skin)
  //   11     → supported, contact detected (HR likely valid)
  const bits = (flags >> 1) & 0b11;
  if (bits === 0b10) return "no_contact";
  if (bits === 0b11) return "contact";
  return "not_supported";
};

export function parseHeartRateMeasurement(
  view: DataView,
): HeartRateMeasurement {
  const out: HeartRateMeasurement = {
    bpm: null,
    sensor_contact: "not_supported",
    energy_kj: null,
    rr_intervals_s: [],
  };

  if (view.byteLength < 1) return out;
  const flags = view.getUint8(0);
  out.sensor_contact = decodeSensorContact(flags);

  let o = 1;
  const is16 = (flags & 0x01) === 0x01;

  if (is16) {
    if (fits(view, o, 2)) {
      out.bpm = view.getUint16(o, true);
      o += 2;
    }
  } else {
    if (fits(view, o, 1)) {
      out.bpm = view.getUint8(o);
      o += 1;
    }
  }

  if ((flags & 0x08) === 0x08) {
    if (fits(view, o, 2)) {
      out.energy_kj = view.getUint16(o, true);
      o += 2;
    }
  }

  if ((flags & 0x10) === 0x10) {
    // RR intervals are a list — read uint16s while bytes remain.
    while (fits(view, o, 2)) {
      const raw = view.getUint16(o, true);
      out.rr_intervals_s.push(raw / 1024);
      o += 2;
    }
  }

  return out;
}
